import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Hillingdon\'s GREEN DfT rating mean I cannot claim?',
    answer: 'No. GREEN means Hillingdon performs above average on network-level condition, spend and much of its maintenance programme — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Their own report admits survey data may not reflect current condition, B and C roads and unclassified roads are surveyed only every two years, and they still expect to patch around 800 potholes in 2025/26.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 574km — 83% of Hillingdon\'s 689.6km carriageway network. These roads are surveyed every two years using AI-led software (with a Detailed Visual Inspection in 2021). At the 2023 survey, 28.4% of U-roads were in amber condition and green-rated roads had fallen to 57.9%. If your incident fell between surveys, the council\'s own data may not have captured the defect at network level.',
  },
  {
    question: 'Why is best practice rated AMBER when everything else is GREEN?',
    answer: 'The DfT best-practice scorecard is separate from condition and spend. Hillingdon\'s transparency report shows general bridge inspections every two years, B/C and U-road condition surveys every two years, and a methodology change in 2022 when AI surveys replaced earlier approaches. The council itself states it is "unable to compare" condition results across differing survey methods — which is exactly the kind of gap a specific, evidence-led claim can exploit.',
  },
  {
    question: 'Hillingdon spends nearly ten times its DfT capital allocation — does that block my claim?',
    answer: 'Not automatically. Projected 2025/26 capital spend is £11.0m against a DfT allocation of £1.152m — a GREEN spend scorecard. But aggregate investment does not prove the individual pothole was known and repaired within inspection intervals. Prior reports, photos showing defect age, and the road\'s place in the council\'s two-year survey cycle matter more than the headline budget.',
  },
  {
    question: 'Pothole repairs rose from 713 to 1,023 in four years — does that help my case?',
    answer: 'It can. Hillingdon filled 713 potholes in 2021/22 and 1,023 in 2024/25 — a 44% rise while DfT rates condition GREEN. That pattern shows defects still form faster than preventative resurfacing prevents them. The council defines a pothole as a defect requiring a patch to make the road safe — so these figures are evidence of ongoing reactive workload, not proof your specific defect was unavoidable.',
  },
  {
    question: 'Does the council\'s admission about data centres and electric buses strengthen a claim?',
    answer: 'Potentially, if your incident is on an affected route. Hillingdon\'s report states electric buses and increased construction traffic for Hayes data centres are causing roads to deteriorate faster, with more patches and trenches affecting lifespan. That is documented knowledge of elevated wear — which raises what a "reasonable" inspection and maintenance regime looks like on those corridors under Section 58.',
  },
];

export function HillingdonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Hillingdon Pothole Claims | 1,023 Repairs on a Green-Rated Network | Fixtyer"
        description="Hillingdon Council earns GREEN DfT ratings but patched 1,023 potholes in 2024/25, surveys 83% of its network every two years, and admits construction traffic is accelerating wear. What that means for your Section 58 claim."
        canonical="/councils/hillingdon"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Hillingdon', url: '/councils/hillingdon' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Hillingdon Council"
        councilSlug="hillingdon"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/hillingdon"
        headline="Hillingdon Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Hillingdon</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            1,023 Potholes Patched in 2024/25 on a Green-Rated Borough
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Hillingdon earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards for condition and spend —
            yet its own transparency report records <strong className="text-rose-400">1,023 pothole repairs in 2024/25</strong>,
            up 44% from 2021/22. Eighty-three per cent of the network is unclassified residential roads surveyed every
            two years, best practice is <strong className="text-amber-400">AMBER</strong>, and the council admits data-centre
            construction traffic is wearing roads faster. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">£11.0m</div>
            <div className="text-white font-medium mt-2">Projected capital spend 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              Against a DfT capital allocation of £1.152m — nearly ten times the grant — while the council still budgets
              17% of highways spend for reactive defect response and expects ~800 more pothole patches this year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            689.6km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Hillingdon's 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">689.6km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">574km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">83% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">73.0km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">56.1km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Heathrow and principal corridors</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Hillingdon highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">1,215km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">129km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">79km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges, culverts and structures</td>
                  <td className="px-6 py-4 text-center font-medium">152 structures (72 bridges, 31 culverts, 49 footbridges)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">~24,000</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">~33,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Principal (A) Roads provide access to London Heathrow Airport and key access points in and out of London, notably towards Reading and the west. B & C roads serve as the main links between the A roads and the unclassified (U) Roads that are predominantly residential quieter roads."
            source="Hillingdon Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What GREEN Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            AI-led surveys from 2022 — with B/C and U-roads measured only every two years
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Hillingdon introduced AI-led condition surveys in 2022, replacing
              earlier approaches. The council states it is "unable to compare the condition survey results due to differing
              survey methods" between the 2021 Detailed Visual Inspection and 2023 AI surveys on B/C and U-roads.
              Survey data also "may not account for changes to the condition of the road, since the survey was taken."
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (56.1km) — surveyed annually via TfL BPRN</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">25.2%</td>
                    <td className="px-4 py-2 text-center">23.4%</td>
                    <td className="px-4 py-2 text-center">51.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">31.1%</td>
                    <td className="px-4 py-2 text-center">23.7%</td>
                    <td className="px-4 py-2 text-center">45.3%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">29.3%</td>
                    <td className="px-4 py-2 text-center font-bold">25.3%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">45.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Principal roads are predominantly green-rated — 51.4% in 2022 and 45.5% in 2024 under annual AI surveys.
              But A-roads are only 8% of the carriageway network, and red-condition A-roads peaked at 31.1% in 2023.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (73.0km) — surveyed every two years</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Survey</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021 (DVI)</td>
                    <td className="px-4 py-2 text-center">16.5%</td>
                    <td className="px-4 py-2 text-center">12.3%</td>
                    <td className="px-4 py-2 text-center">71.2%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2023 (AI)</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">5.9%</td>
                    <td className="px-4 py-2 text-center font-bold">18.5%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">75.6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C roads look stronger under AI surveys — but the council warns the 2021 and 2023 figures use different
              methodologies and cannot be directly compared.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (574km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Survey</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021 (DVI)</td>
                    <td className="px-4 py-2 text-center">14.2%</td>
                    <td className="px-4 py-2 text-center">17.6%</td>
                    <td className="px-4 py-2 text-center">68.2%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2023 (AI)</td>
                    <td className="px-4 py-2 text-center font-bold">13.7%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">28.4%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">57.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Under AI surveys, amber-condition U-roads rose from 17.6% to 28.4% and green-rated U-roads fell from
              68.2% to 57.9%. That is roughly 163km of residential network in amber condition at the last AI survey —
              on roads surveyed only every other year.
            </p>
          </div>

          <QuoteCard
            quote="The reported condition of the road network is based on data collected at a specific time. This may not account for changes to the condition of the road, since the survey was taken."
            source="Hillingdon Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but reactive maintenance still accounts for roughly a quarter of highways budgets
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Hillingdon highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">km resurfaced</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">1,152</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">11,000</td>
                  <td className="px-4 py-3 text-center">3,244</td>
                  <td className="px-4 py-3 text-center">11.0</td>
                  <td className="px-4 py-3 text-center text-emerald-400">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">354</td>
                  <td className="px-4 py-3 text-center">8,783</td>
                  <td className="px-4 py-3 text-center">3,144</td>
                  <td className="px-4 py-3 text-center font-bold">17.3</td>
                  <td className="px-4 py-3 text-center">74%</td>
                  <td className="px-4 py-3 text-center">26%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">354</td>
                  <td className="px-4 py-3 text-center">6,781</td>
                  <td className="px-4 py-3 text-center">3,125</td>
                  <td className="px-4 py-3 text-center">13.5</td>
                  <td className="px-4 py-3 text-center">68%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">4,533</td>
                  <td className="px-4 py-3 text-center">3,017</td>
                  <td className="px-4 py-3 text-center">13.0</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">8,996</td>
                  <td className="px-4 py-3 text-center">3,083</td>
                  <td className="px-4 py-3 text-center">17.3</td>
                  <td className="px-4 py-3 text-center">74%</td>
                  <td className="px-4 py-3 text-center">26%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">6,617</td>
                  <td className="px-4 py-3 text-center">2,826</td>
                  <td className="px-4 py-3 text-center">19.4</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Capital spend has risen from £4.5m in 2022/23 to a projected £11.0m in 2025/26, while preventative
                maintenance share climbed from 60% to a projected 77%. The council invests far beyond its DfT capital
                allocation — a credible asset-management story on paper.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Even with 77% preventative spend planned, the council budgets 17% of this year's highways programme
                for reactive defect response and day-to-day safety work — and expects ~800 pothole patches. Spend
                volume does not prove every defect was caught within inspection intervals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Patch Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — defects the council defines as requiring a patch to make the road safe
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Hillingdon estimated potholes filled 2021-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">713</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">844</td>
                  <td className="px-6 py-4 text-center text-amber-600">+18.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">1,021</td>
                  <td className="px-6 py-4 text-center text-amber-600">+43.1%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+43.6%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">~800</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="A pothole is a defect that has been identified on our roads, which requires a patch on the road to make it safe for road users."
              source="Hillingdon Council Local Highways Maintenance Transparency Report 2025"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Nearly three patches a day</h3>
              <p className="text-surface-600 text-sm">
                1,023 pothole repairs in 2024/25 works out to roughly 2.8 patches per day across 689.6km. A
                green-rated network still producing that volume of safety-critical defects is one where individual
                potholes routinely form between surveys — especially on U-roads measured every two years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Hillingdon says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> annual AI-led surveys via TfL BPRN programme</li>
                <li>• <strong>B/C and U roads:</strong> every two years (next survey in 2025)</li>
                <li>• <strong>Structures:</strong> general inspections every two years; principal inspections every six years</li>
                <li>• <strong>Gullies:</strong> annual cleansing regime; high flood/leaf-fall areas more frequently</li>
                <li>• <strong>Street lighting:</strong> structural testing every five years; electrical testing every six years</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Safety inspections</h3>
              <p className="text-surface-600 text-sm mb-3">
                Hillingdon carries out routine safety inspections defined by a network hierarchy considering vehicular
                and pedestrian volumes, transport hubs, emergency-service access and education facilities. Response
                times to fix safety-critical defects are set by the same hierarchy.
              </p>
              <p className="text-surface-600 text-sm">
                Annual programmes prioritise schemes using condition surveys, hierarchy, historical defect frequency,
                accident claims records and resident feedback.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Hillingdon must
              maintain public highways. The council cites this duty explicitly. To defend a claim under
              <strong className="text-white"> Section 58</strong>, it must show a reasonable system for inspecting
              and repairing the specific defect — not just publish a green DfT scorecard.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the two-year B/C or U-road survey cycle — and had it been surveyed recently?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council portal) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="We are unable to compare the condition survey results due to differing survey methods, as shown in Figure 3 and Figure 4."
            source="Hillingdon Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Hillingdon says it will deliver this financial year
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">11km</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Road resurfacing (max)</div>
              <div className="text-xs text-emerald-700 mt-1">5km confirmed + 6km provisional</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">12km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Footway repairs</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">5</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Bridges and structures</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">~800</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes expected</div>
            </div>
          </div>

          <QuoteCard
            quote="For this financial year 2025/26, we have confirmed plans to resurface 5 kilometres of our roads, with provisional agreement to resurface an additional 6 kilometres. For our footways, we plan to repair 12 kilometres of our network."
            source="Hillingdon Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="mt-6 bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Even at the maximum 11km resurfacing programme, Hillingdon would treat roughly 1.6% of its 689.6km
              carriageway network in a single year. The remaining 98%+ relies on reactive patching, routine inspections
              and the two-year condition survey cycle to catch deterioration — on a network where U-road amber
              condition already sits at 28.4%.
            </p>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Hillingdon Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On accelerated wear from electric buses</h3>
              <QuoteCard
                quote="Electric vehicles are heavier than traditional vehicles, due to the additional weight they carry from their batteries. This additional weight contributes to our roads deteriorating faster, particularly in areas where there is frequent stopping and starting like bus stops."
                source="Hillingdon Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On Hayes data centres and construction traffic</h3>
              <QuoteCard
                quote="A number of data centres are currently being built across our borough, specifically in Hayes. While they bring positive benefits by supporting technological advancement, they also place additional stress on our roads This is due to utility providers upgrading their infrastructure to meet the needs of the data centres, which sits beneath our roads. This is resulting in more patches and trenches on our roads, affecting the lifespan of our roads. Additionally, increased construction traffic is required to deliver materials to these sites which is also causing our roads to deteriorate faster."
                source="Hillingdon Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On statutory duty vs planned works</h3>
              <QuoteCard
                quote="We aim to prioritise planned improvements; however, we have a statutory duty under the Highways Act (1980) to keep the highway safe and accessible to the public. This means we will have to respond to defects on our network when they appear."
                source="Hillingdon Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Green-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Hillingdon is not Waltham Forest — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition, spend and overall DfT ratings
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend ~10× DfT allocation (£11.0m vs £1.152m projected)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 77% preventative spend planned for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AI-led surveys aligned with TfL BPRN on principal roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Risk-based asset management aligned to Well-Managed Highway Infrastructure code
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-prepared Section 58 defence. Generic "council neglect" arguments will not land.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best-practice scorecard from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 574km of U-roads — 83% of network — surveyed every two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road amber condition at 28.4% at last AI survey; green U-roads down to 57.9%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 1,023 pothole patches in 2024/25 — up 44% since 2021/22
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits survey snapshots may not reflect current road condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Documented faster deterioration from data-centre construction and electric buses
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN scorecards and £11m capital budgets, your claim lives or dies on the
              <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the two-year survey gap is your strongest structural argument</li>
              <li>• Location near Hayes data-centre corridors or electric bus routes if wear is accelerated</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Hillingdon's
              own transparency data where it helps you — without pretending the council is failing overall.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Hillingdon?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A green-rated borough demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No two-year U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No Hayes corridor wear analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road amber deterioration documented</li>
                <li>✅ Two-year survey cycle argued</li>
                <li>✅ 1,023 pothole repairs in 2024/25 cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Hillingdon</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=hillingdon"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Hillingdon Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.hillingdon.gov.uk/article/14700/Local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Hillingdon Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
